import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import {
  CalendarClock,
  Coins,
  Flag,
  Medal,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "wouter";

import { api } from "@/api";
import { trackTitle } from "@/features/racing/venue";
import {
  Button,
  countdown,
  EmptyState,
  ErrorState,
  HorsePortrait,
  money,
  Panel,
  Skeleton,
  TierStars,
} from "@/components/ui";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";
import { useSession } from "@/session/SessionProvider";

import { ActivityHistory } from "./ActivityHistory";
import { HorseDetailDrawer } from "./HorseDetailDrawer";
import { horseNetCents, type StableResponse } from "./types";

type CouponResponse = {
  coupons: Array<{
    couponId: string;
    type: string;
    stakeCents: number;
    potentialCents: number;
    status: string;
    result: string | null;
    returnedCents: number;
    placedAt: string;
    legs: Array<{
      raceId: string;
      betType: string;
      sel: number[];
      oddsH: number;
      grade: string;
    }>;
  }>;
  nextCursor: string | null;
};

type AwardResponse = {
  awards: Array<{
    periodId: string;
    kind: string;
    amountCents: number;
    status: string;
  }>;
};

const TABS = [
  ["racehorses", "Racehorses"],
  ["activity", "Transactions"],
  ["bets", "Bet history"],
  ["awards", "Awards"],
] as const;

export default function PlayerPage() {
  const params = useParams<{ tab?: string; horseId?: string }>();
  const [, navigate] = useLocation();
  const { session, requestLogin } = useSession();
  useRealtimeTopic(session ? `player.${session.playerId}` : null);
  const [selectedHorseId, setSelectedHorseId] = useState<string | null>(
    params.horseId ?? null,
  );
  const tab = TABS.some(([value]) => value === params.tab)
    ? params.tab!
    : "racehorses";

  const profile = useQuery({
    queryKey: ["player", session?.playerId],
    queryFn: api.player,
    enabled: Boolean(session),
    refetchInterval: 30_000,
  });
  const stable = useQuery({
    queryKey: ["stable", session?.playerId],
    queryFn: () => api.stable(),
    enabled: Boolean(session),
    refetchInterval: 15_000,
  });
  const standings = useQuery({
    queryKey: ["standings", "season", session?.playerId],
    queryFn: () => api.ownershipLeaderboard("season"),
    enabled: Boolean(session),
    refetchInterval: 60_000,
  });
  const bets = useQuery({
    queryKey: ["bets", session?.playerId],
    queryFn: () => api.myCoupons() as Promise<CouponResponse>,
    enabled: Boolean(session) && tab === "bets",
    refetchInterval: 10_000,
  });
  const awards = useQuery({
    queryKey: ["awards", session?.playerId],
    queryFn: () => api.myAwards() as Promise<AwardResponse>,
    enabled: Boolean(session) && tab === "awards",
    refetchInterval: 30_000,
  });

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  if (!session) {
    return (
      <div className="page page--player">
        <Panel>
          <EmptyState
            action={<Button onClick={requestLogin}>Sign in</Button>}
            detail="Your stable, wagers, awards and ownership actions live here."
            title="Sign in to view your stable"
          />
        </Panel>
      </div>
    );
  }

  const stableRows = (stable.data as StableResponse | undefined)?.stable ?? [];
  const nextRace = stableRows
    .filter((horse) => !horse.isRetired && horse.nextRaceAt)
    .sort(
      (a, b) => Date.parse(a.nextRaceAt ?? "") - Date.parse(b.nextRaceAt ?? ""),
    )[0];
  const viewer = standings.data?.viewer ?? null;
  const pnl = profile.data?.pnl;

  return (
    <div className="page page--player">
      {profile.error ? (
        <Panel aria-label="Player profile">
          {profile.data ? (
            <p className="form-note">Showing the last loaded profile.</p>
          ) : null}
          <ErrorState
            error={profile.error}
            retry={() => void profile.refetch()}
          />
        </Panel>
      ) : null}
      {profile.isPending ? (
        <Skeleton variant="profile" />
      ) : profile.data ? (
        <>
          <Panel className="player-hero">
            <span className="player-hero__avatar">
              {profile.data.displayName.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <h1>{profile.data.displayName}</h1>
              <span>Player ID: {profile.data.playerId}</span>
              <small>
                <Trophy aria-hidden /> Season member ·{" "}
                {new Date(profile.data.memberSince).toLocaleDateString()}
              </small>
            </div>
            <div className="player-hero__links">
              <Link href="/app/marketplace">Marketplace</Link>
              <Link href="/app/syndicates">Syndicates</Link>
            </div>
          </Panel>

          <div className="player-stats">
            <Stat
              icon={<Flag />}
              label="Active horses · race automatically"
              value={profile.data.stats.activeHorses}
            />
            <Stat
              icon={<CalendarClock />}
              label={
                nextRace
                  ? `Next race · ${nextRace.name}`
                  : !stable.data
                    ? "Next race · schedule unavailable"
                    : "Next race · schedule pending"
              }
              value={
                nextRace?.nextRaceAt ? countdown(nextRace.nextRaceAt, now) : "—"
              }
            />
            <Stat
              icon={<Medal />}
              label={
                viewer && viewer.projectedCents > 0
                  ? `Season points · ≈${money(viewer.projectedCents, profile.data.currency)} projected`
                  : "Season points"
              }
              value={
                viewer
                  ? viewer.totalPoints.toFixed(1)
                  : standings.data
                    ? "0.0"
                    : "—"
              }
            />
            <Stat
              icon={<Coins />}
              label="Total earnings"
              value={money(
                profile.data.stats.totalEarningsCents,
                profile.data.currency,
              )}
            />
            <Stat
              icon={<TrendingUp />}
              label="Stable net P&L"
              value={
                pnl ? (
                  <span
                    className={
                      pnl.netCents >= 0 ? "pnl pnl--up" : "pnl pnl--down"
                    }
                  >
                    {money(pnl.netCents, profile.data.currency)}
                  </span>
                ) : (
                  "—"
                )
              }
            />
            <Stat
              icon={<Trophy />}
              label={`Win rate · ${profile.data.stats.totalWins} wins`}
              value={`${(profile.data.stats.winRateBp / 100).toFixed(1)}%`}
            />
          </div>
        </>
      ) : null}
      {standings.error ? (
        <Panel aria-label="Season points">
          {standings.data ? (
            <p className="form-note">Showing the last loaded season points.</p>
          ) : null}
          <ErrorState
            error={standings.error}
            retry={() => void standings.refetch()}
          />
        </Panel>
      ) : null}

      <div className="player-tabs">
        {TABS.map(([value, label]) => (
          <button
            className={clsx(tab === value && "is-active")}
            key={value}
            onClick={() => navigate(`/app/player/${value}`)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "racehorses" ? (
        <StableList
          data={stable.data as StableResponse | undefined}
          loading={stable.isPending}
          error={stable.error}
          retry={() => void stable.refetch()}
          now={now}
          onSelect={setSelectedHorseId}
        />
      ) : null}
      {tab === "activity" ? (
        <ActivityHistory playerId={session.playerId} />
      ) : null}
      {tab === "bets" ? (
        <BetHistory
          data={bets.data}
          loading={bets.isPending}
          error={bets.error}
          retry={() => void bets.refetch()}
        />
      ) : null}
      {tab === "awards" ? (
        <Awards
          data={awards.data}
          loading={awards.isPending}
          error={awards.error}
          retry={() => void awards.refetch()}
        />
      ) : null}

      {selectedHorseId ? (
        <HorseDetailDrawer
          horseId={selectedHorseId}
          onClose={() => setSelectedHorseId(null)}
          stableRow={stableRows.find(
            (horse) => horse.horseId === selectedHorseId,
          )}
        />
      ) : null}
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  return (
    <Panel>
      {icon}
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </Panel>
  );
}

function StableList({
  data,
  loading,
  error,
  retry,
  now,
  onSelect,
}: {
  data?: StableResponse;
  loading: boolean;
  error: unknown;
  retry: () => void;
  now: number;
  onSelect: (horseId: string) => void;
}) {
  const [showRetired, setShowRetired] = useState(false);
  if (loading) return <Skeleton rows={5} variant="stable" />;
  const rows = (data?.stable ?? []).filter(
    (horse) => horse.isRetired === showRetired,
  );
  return (
    <Panel className="stable-list" aria-label="Racehorses">
      {error ? (
        <>
          {data ? (
            <p className="form-note">Showing the last loaded stable.</p>
          ) : null}
          <ErrorState error={error} retry={retry} />
        </>
      ) : null}
      {data ? (
        <>
          <header>
            <div>
              <button
                className={clsx(!showRetired && "is-active")}
                onClick={() => setShowRetired(false)}
                type="button"
              >
                Active (
                {
                  (data?.stable ?? []).filter((horse) => !horse.isRetired)
                    .length
                }
                )
              </button>
              <button
                className={clsx(showRetired && "is-active")}
                onClick={() => setShowRetired(true)}
                type="button"
              >
                Retired (
                {(data?.stable ?? []).filter((horse) => horse.isRetired).length}
                )
              </button>
            </div>
            <span>{rows.length} shares</span>
          </header>
          {showRetired ? null : (
            <p className="stable-autoenroll">
              Your horses race automatically — no setup needed. Fields are drawn
              fairly from the published schedule.
            </p>
          )}
          {rows.length ? (
            <div className="stable-list__grid">
              {rows.map((horse) => {
                const net = horseNetCents(horse);
                return (
                  <button
                    className="stable-horse-row"
                    key={horse.shareId}
                    onClick={() => onSelect(horse.horseId)}
                    type="button"
                  >
                    <HorsePortrait
                      compact
                      horse={{
                        horseId: horse.horseId,
                        name: horse.name,
                        tier: horse.tier,
                        appearance: horse.appearance ?? {},
                      }}
                    />
                    <div>
                      <strong>{horse.name}</strong>
                      <TierStars tier={horse.tier} />
                      <span>
                        {trackTitle(horse)} · {horse.archetype} ·{" "}
                        {horse.career.races} races · {horse.career.form || "—"}
                      </span>
                      <span
                        className={clsx(
                          "stable-next",
                          horse.nextRaceAt && "stable-next--scheduled",
                        )}
                      >
                        {horse.isRetired
                          ? "Retired"
                          : horse.nextRaceAt
                            ? `Next race in ${countdown(horse.nextRaceAt, now)}`
                            : "Next race pending — races automatically"}
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>Record</dt>
                        <dd>
                          {horse.career.wins}-{horse.career.places ?? 0}-
                          {horse.career.shows ?? 0}
                        </dd>
                      </div>
                      <div>
                        <dt>Your net</dt>
                        <dd
                          className={net >= 0 ? "pnl pnl--up" : "pnl pnl--down"}
                        >
                          {money(net)}
                        </dd>
                      </div>
                      <div>
                        <dt>Shares</dt>
                        <dd>{horse.shareBp / 100}%</dd>
                      </div>
                      {horse.quoteCents != null ? (
                        <div
                          className={clsx(
                            "stable-quote",
                            horse.quoteKind === "window" &&
                              "stable-quote--window",
                          )}
                        >
                          <dt>
                            {horse.quoteKind === "window"
                              ? "Cash today"
                              : "Sell now"}
                          </dt>
                          <dd>{money(horse.quoteCents)}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyState
              action={<Link href="/app/packs">Open a pack</Link>}
              title={showRetired ? "No retired horses" : "Your stable is empty"}
            />
          )}
        </>
      ) : null}
    </Panel>
  );
}

function BetHistory({
  data,
  loading,
  error,
  retry,
}: {
  data?: CouponResponse;
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  if (loading) return <Skeleton rows={8} variant="table" />;
  return (
    <Panel className="history-table" aria-label="Bet history">
      {error ? (
        <>
          {data ? (
            <p className="form-note">Showing the last loaded bets.</p>
          ) : null}
          <ErrorState error={error} retry={retry} />
        </>
      ) : null}
      {data?.coupons.length ? (
        <table>
          <thead>
            <tr>
              <th>Placed</th>
              <th>Type</th>
              <th>Selections</th>
              <th>Stake</th>
              <th>Return</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {data.coupons.map((coupon) => (
              <tr key={coupon.couponId}>
                <td>{new Date(coupon.placedAt).toLocaleString()}</td>
                <td>{coupon.type}</td>
                <td>
                  {coupon.legs
                    .map(
                      (leg) =>
                        `${leg.betType.toUpperCase()} ${leg.sel.map((gate) => gate + 1).join("-")}`,
                    )
                    .join(" / ")}
                </td>
                <td>{money(coupon.stakeCents)}</td>
                <td>
                  {coupon.status === "settled"
                    ? money(coupon.returnedCents)
                    : `to win ${money(coupon.potentialCents)}`}
                </td>
                <td>
                  <span
                    className={`history-result history-result--${coupon.result ?? coupon.status}`}
                  >
                    {coupon.result ?? coupon.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : data ? (
        <EmptyState title="No wagers yet" />
      ) : null}
    </Panel>
  );
}

function Awards({
  data,
  loading,
  error,
  retry,
}: {
  data?: AwardResponse;
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  if (loading) return <Skeleton rows={3} variant="rooms" />;
  return (
    <Panel className="awards-list" aria-label="Awards">
      {error ? (
        <>
          {data ? (
            <p className="form-note">Showing the last loaded awards.</p>
          ) : null}
          <ErrorState error={error} retry={retry} />
        </>
      ) : null}
      {data?.awards.length ? (
        data.awards.map((award) => (
          <article key={`${award.periodId}:${award.kind}`}>
            <Trophy aria-hidden />
            <div>
              <strong>{award.kind.replaceAll("_", " ")}</strong>
              <span>{award.status}</span>
            </div>
            <b>{money(award.amountCents)}</b>
          </article>
        ))
      ) : data ? (
        <EmptyState title="No awards yet" />
      ) : null}
    </Panel>
  );
}
